// زرّ «الشريط المتحرك» — يكتب به المالك الرسالة التي تمشي أمام الجميع (0071).
//
// مستقلٌّ عن «الإعلان»: ذاك رسالةٌ تصل جرس كل موظف ويقرؤها متى فتح الجرس، وهذا **سطرٌ واحد
// يبقى ماشيًا أمام العين** طول اليوم. فلا يُخلط بينهما، ولكلٍّ زرّه.
//
// **ولا تعديل ولا حذف**: كل حفظٍ نسخةٌ جديدة، وأحدثها هو الظاهر — فيبقى تاريخ ما عُرض على
// الناس مكتوبًا. والإطفاء نسخةٌ بـ«موقوف»، ونصُّها يبقى ليُعاد تشغيله بضغطة.
//
// والخادم يفرض أن الأدمن وحده يكتب — فظهور الزرّ ترتيبٌ للواجهة لا حماية.
import { useCallback, useEffect, useState } from 'react';
import { ApiError, get, post, type TickerHistoryRow, type TickerMessage } from '../lib/api.ts';
import { Alert, Badge, Drawer, Field, TextArea, TextInput } from '../components/ui.tsx';
import { cairoDateTimeAr } from '../lib/format.ts';
import { TICKER_SAVED_EVENT } from '../components/TickerBar.tsx';

const MAX = 300;
const MIN_SPEED = 5;
const MAX_SPEED = 60;

/** وصفٌ عربي لرقم الثواني — يعرف الأدمن معنى الرقم بلمحة لا بحسابٍ في رأسه. */
function speedLabel(sec: number): string {
  if (sec <= 10) return 'سريع';
  if (sec <= 25) return 'متوسط';
  if (sec <= 40) return 'بطيء';
  return 'بطيء جدًّا';
}

export function TickerButton() {
  const [open, setOpen] = useState(false);
  const [body, setBody] = useState('');
  const [link, setLink] = useState('');
  const [active, setActive] = useState(true);
  const [speed, setSpeed] = useState(18);
  const [current, setCurrent] = useState<TickerMessage | null>(null);
  const [history, setHistory] = useState<TickerHistoryRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [cur, hist] = await Promise.all([
        get<TickerMessage>('/ticker'),
        get<{ items: TickerHistoryRow[] }>('/admin/ticker/history'),
      ]);
      setCurrent(cur); setHistory(hist.items);
      setBody(cur.body); setLink(cur.link ?? ''); setActive(cur.active); setSpeed(cur.speedSeconds ?? 18);
    } catch (e) { setError(e instanceof ApiError ? e.message : 'تعذّر تحميل الشريط'); }
  }, []);

  useEffect(() => { if (open) void load(); }, [open, load]);

  const linkOk = link.trim() === '' || /^https:\/\/\S{4,}$/.test(link.trim());
  const ready = (!active || body.trim().length >= 2) && body.trim().length <= MAX && linkOk;

  async function save() {
    if (!ready) return;
    setBusy(true); setError(null); setDone(null);
    try {
      await post<TickerMessage>('/admin/ticker', {
        body: body.trim(), active, link: link.trim() === '' ? null : link.trim(), speedSeconds: speed,
      });
      setDone(active ? 'الشريط يعمل الآن، ويظهر لكل من يفتح النظام.' : 'أُوقف الشريط، ولن يظهر لأحد.');
      // يُبثّ فورًا ليتحدَّث شريط الصفحة نفسه بلا انتظار دورته — فيرى الأدمن التغيير أمامه
      // مباشرةً ولا يظنّ أن الحفظ لم يعمل فيكرّره (انظر التعليق في TickerBar.tsx).
      window.dispatchEvent(new Event(TICKER_SAVED_EVENT));
      await load();
    } catch (e) { setError(e instanceof ApiError ? e.message : 'تعذّر حفظ الشريط'); }
    finally { setBusy(false); }
  }

  return (
    <>
      <button type="button" className="btn-ghost px-3 py-1.5 text-sm" onClick={() => setOpen(true)}>
        الشريط المتحرك
      </button>

      {open && (
        <Drawer title="الشريط المتحرك" onClose={() => setOpen(false)} width="lg">
          <div className="space-y-4">
            <p className="text-sm text-ink-600">
              سطرٌ واحد يمشي أمام كل من يفتح النظام — بياعًا وأدمن. اكتبه هنا، وشغّله أو أوقفه.
              وكل حفظٍ نسخةٌ جديدة: لا يُعدَّل القديم ولا يُحذف.
            </p>

            {error && <Alert>{error}</Alert>}
            {done && <Alert kind="success">{done}</Alert>}

            {current && (
              <div className="rounded-xl border border-slate-200 p-3">
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className="font-semibold text-ink-700">الظاهر الآن</span>
                  {current.active
                    ? <Badge tone="bg-emerald-50 text-emerald-800">يعمل</Badge>
                    : <Badge tone="bg-slate-100 text-ink-600">موقوف</Badge>}
                  {current.updatedAt && <span className="text-ink-500">{cairoDateTimeAr(current.updatedAt)}</span>}
                  <span className="text-ink-500">· سرعته {speedLabel(current.speedSeconds)} ({current.speedSeconds} ث)</span>
                </div>
                <p className="mt-2 text-sm text-ink-900">{current.body || '— لا نصّ —'}</p>
              </div>
            )}

            <Field label="نصّ الشريط" required={active}
              hint={`${body.trim().length} من ${MAX} حرفًا · النجمة ★ تفصل بين رسالةٍ وأخرى`}>
              <TextArea rows={3} value={body} maxLength={MAX} onChange={e => setBody(e.target.value)}
                placeholder="بادر بالحجز — أسعار مميزة على رحلات القاهرة ★ تغيير الموعد يغيّر السعر" />
            </Field>

            {/* النجمة ليست على لوحة مفاتيح عربية، فيُدرجها زرٌّ بدل البحث عنها في لوحة الرموز */}
            <button type="button" className="btn-ghost px-3 py-1 text-xs"
              onClick={() => setBody(b => (b.trimEnd() + ' ★ ').slice(0, MAX))}>
              إضافة فاصل ★ — رسالة جديدة في نفس الشريط
            </button>

            <Field label="رابط عند الضغط (اختياري)" error={linkOk ? null : 'الرابط يبدأ بـ https://'}>
              <TextInput value={link} dir="ltr" onChange={e => setLink(e.target.value)} placeholder="https://…" />
            </Field>

            <Field label="سرعة الحركة" hint={`${speed} ثانية للدورة الكاملة — ${speedLabel(speed)} · كلّما زاد الرقم كانت أبطأ`}>
              <div className="flex items-center gap-3">
                <span className="shrink-0 text-xs text-ink-500">أسرع</span>
                <input type="range" min={MIN_SPEED} max={MAX_SPEED} step={1} value={speed}
                  onChange={e => setSpeed(Number(e.target.value))}
                  className="h-2 w-full flex-1 cursor-pointer accent-brand-600" />
                <span className="shrink-0 text-xs text-ink-500">أبطأ</span>
              </div>
            </Field>

            <label className="flex items-center gap-2 text-sm text-ink-800">
              <input type="checkbox" checked={active} onChange={e => setActive(e.target.checked)} />
              الشريط يعمل ويظهر للجميع
            </label>

            <div className="flex gap-2">
              <button type="button" className="btn-primary px-5 py-2" disabled={!ready || busy}
                onClick={() => void save()}>
                {busy ? 'جارٍ الحفظ…' : 'حفظ'}
              </button>
              <button type="button" className="btn-ghost px-5 py-2" onClick={() => setOpen(false)}>إغلاق</button>
            </div>

            {history.length > 0 && (
              <div>
                <h3 className="mb-2 text-sm font-semibold text-ink-900">النسخ السابقة</h3>
                <ul className="space-y-2">
                  {history.map(h => (
                    <li key={h.id} className="rounded-lg border border-slate-200 p-2 text-xs">
                      <div className="flex flex-wrap items-center gap-2">
                        {h.active
                          ? <Badge tone="bg-emerald-50 text-emerald-800">يعمل</Badge>
                          : <Badge tone="bg-slate-100 text-ink-600">موقوف</Badge>}
                        <span className="text-ink-500">{cairoDateTimeAr(h.createdAt)}</span>
                        {h.createdByName && <span className="text-ink-500">· {h.createdByName}</span>}
                        <span className="text-ink-500">· {speedLabel(h.speedSeconds)} ({h.speedSeconds} ث)</span>
                      </div>
                      <p className="mt-1 text-ink-800">{h.body || '— لا نصّ —'}</p>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </Drawer>
      )}
    </>
  );
}
